import { type FC, type ComponentProps, useRef, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { HpSharedFeaturedModuleContent } from './HpSharedFeaturedModuleContent';

import { VIDEO_COMPONENT_TEST_NAME } from '../helpers/abTestVideoComponent';

import { type HpSharedFeaturedModule_viewer$key } from './__generated__/HpSharedFeaturedModule_viewer.graphql';
import { type HpSharedFeaturedModule_user$key } from './__generated__/HpSharedFeaturedModule_user.graphql';

const viewerFragment = graphql`
    fragment HpSharedFeaturedModule_viewer on Viewer {
        ...HpSharedFeaturedModuleContent_viewer
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModule_user on User {
        ...HpSharedFeaturedModuleContent_user
    }
`;

export const HpSharedFeaturedModule: FC<{
    viewer: HpSharedFeaturedModule_viewer$key | null;
    user?: HpSharedFeaturedModule_user$key | null;
    isClient: boolean;
}> = ({ viewer: viewerRef, user: userRef, isClient }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const elementRef = useRef<HTMLDivElement>(null);

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(({ isVisible }) => {
            if (isVisible) {
                // We want to track the video component test (TOF-2527) when featured module is visible.
                // Video component goes right after featured module but video component is not visible on control at all.
                // So we rely on featured module visibility.
                trackAbTestV2Variant(VIDEO_COMPONENT_TEST_NAME);
            }
        }, []);

    //isClient ensures that the module is rendered only after client side refetch
    if (!isClient) {
        return null;
    }

    return (
        <div ref={elementRef}>
            <VisibilityTracker elementRef={elementRef} onVisibilityChange={onVisibilityChange} />
            <HpSharedFeaturedModuleContent viewer={viewer} user={user} />
        </div>
    );
};
